import React from "react";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { useManageAccountsDrawer } from "@web-src/modules/client-support/pages/ClientSupportPage/components/ManageAccountsDrawer/providers/ManageAccountsDrawerContext";
import { resolveChatBotSettings } from "@jugl-web/domain-resources/chat-bot/utils/resolveChatBotSettings";
import { getUniqueId, useTranslations } from "@jugl-web/utils";
import { ChatBotChannelPreview } from "@web-src/modules/client-support/pages/ClientSupportPage/components/ManageAccountsDrawer/screens/WebChatBotAccountScreen/components/ChatBotChannelPreview";
import { ReactComponent as WebChatIcon } from "./assets/web-chat.svg";

export const WebChatPagePreview: React.FC<{ entityId: string }> = ({
  entityId,
}) => {
  const { t } = useTranslations();
  const { integrationsApi } = useRestApiProvider();
  const { data } = integrationsApi.useGetAiAgentConfigQuery({ entityId });
  const { crmConfig } = useManageAccountsDrawer();
  const chatBotSettings = resolveChatBotSettings(crmConfig?.chatbot?.settings);

  return (
    <div className="relative h-full w-full">
      <div className="absolute right-0 bottom-0 z-10 h-full">
        <ChatBotChannelPreview
          entityId={entityId}
          chatName={chatBotSettings.chatName}
          agentName={chatBotSettings.agentName}
          welcomeMessage={chatBotSettings.welcomeMessage}
          senderMessageBackgroundColor={
            chatBotSettings.senderMessageBackgroundColor
          }
          theme={chatBotSettings.theme}
          buttonColor={chatBotSettings.buttonColor}
          alignment={chatBotSettings.alignment}
          bottomSpacing={chatBotSettings.bottomSpacing}
          sideSpacing={chatBotSettings.sideSpacing}
          chatImage={chatBotSettings.chatImageUrl}
          agentImage={chatBotSettings.agentImage}
          buttonIcon={chatBotSettings.buttonIcon}
          ctaVisual={chatBotSettings.ctaVisual?.url ?? null}
          ctas={chatBotSettings.ctas.map((item) => ({
            id: getUniqueId(),
            value: item,
          }))}
          suggestions={chatBotSettings.suggestions.map((item) => ({
            id: getUniqueId(),
            value: item,
          }))}
          chatMode="active"
          previewMode="default"
          isPreview
        />
      </div>
      <div className="flex h-full w-full flex-col items-center justify-center overflow-hidden p-10">
        <div className="flex w-full shrink-0 items-center gap-4 px-8 py-6">
          {chatBotSettings.chatImageUrl ? (
            <img
              src={chatBotSettings.chatImageUrl}
              alt=""
              className="h-10 w-10 shrink-0 rounded-full object-cover opacity-20"
            />
          ) : (
            <WebChatIcon className="shrink-0 opacity-20" />
          )}
          <span className="font-secondary text-[32px] font-extrabold text-dark-100">
            {data?.config.business_name ??
              t({
                id: "cx-ai-agents-page.web-chat-preview",
                defaultMessage: "Web Chat Preview",
              })}
          </span>
        </div>

        <div className="flex min-h-0 w-full flex-1 flex-col gap-5 overflow-hidden px-8 pb-6">
          <div className="h-10 w-[60%] shrink-0 rounded-xl bg-[#FAFAFA]" />

          <div className="flex w-full shrink-0 flex-col gap-3">
            <div className="h-3.5 w-full rounded-lg bg-[#FAFAFA]" />
            <div className="h-3.5 w-[92%] rounded-lg bg-[#FAFAFA]" />
            <div className="h-3.5 w-[84%] rounded-lg bg-[#FAFAFA]" />
          </div>

          <div className="h-10 w-[34%] shrink-0 rounded-xl bg-[#FAFAFA]" />

          <div className="flex w-full shrink-0 flex-col gap-3">
            <div className="h-3.5 w-full rounded-lg bg-[#FAFAFA]" />
            <div className="h-3.5 w-[95%] rounded-lg bg-[#FAFAFA]" />
            <div className="h-3.5 w-[88%] rounded-lg bg-[#FAFAFA]" />
          </div>

          <div className="grid min-h-0 w-full flex-1 grid-cols-3 gap-6 overflow-hidden">
            <div className="h-full rounded-2xl bg-[#FAFAFA]" />
            <div className="h-full rounded-2xl bg-[#FAFAFA]" />
            <div className="h-full rounded-2xl bg-[#FAFAFA]" />
          </div>
        </div>
      </div>
    </div>
  );
};
